import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronUp, Star } from "lucide-react";
import { api } from "@/services/api";
import {
  sidebarConfig,
  getMenuGroups,
  getMenuItems,
  getOrganisationItem,
  UserRole,
  type SidebarGroupConfig,
  type SidebarItemConfig,
} from "@/pages/sidebar.config";
import { useAdvisorReviewStatus } from "@/hooks/useAdvisorReviewStatus";
import { useAuth } from "@/hooks/useAuth";
import { orgProgramKeys, organisationProgramApi } from "@/services/organisationProgramApi";
import { peerAdvisoryApi } from "@/services/peerAdvisoryApi";
import { hasRole } from "@/utils/roleUtils";
import { cn } from "@/utils/cn";
import { GettingStartedDock } from "@/pages/gettingStarted/components/GettingStartedDock";

/**
 * Achiever/Expert workspace tabs. Clicking the inactive Expert tab switches
 * activeWorkspace via PATCH /users/me/active-workspace, if the user actually
 * holds that role -- otherwise it stays disabled. An achiever gets the
 * advisor role when they submit their Expert Application (admins already
 * chose who may apply), not when it is approved. The amber star flags an
 * advisor whose application isn't APPROVED yet, mirroring the Application
 * Status card on Home.
 *
 * Becoming an achiever needs no approval, so the Achiever tab is always
 * clickable even before the role is granted -- clicking it grants the role
 * and switches over in one step (same two calls as AdvisorFTUXPage's
 * ExploreAchieverCard). That FTUX card is a one-time, easy-to-miss prompt;
 * without this, an expert who skipped it (or whose application got approved
 * and moved them straight to the redesigned Advisor Home, past FTUX
 * entirely) would have no way to ever reach the Achiever workspace again.
 *
 * Visibility: an achiever-only user (no advisor role yet) never sees this
 * toggle at all, just the default achiever menu. It appears once they hold
 * the advisor role too (either because they signed up as an Expert, or
 * they submitted an Expert Application).
 *
 * Order: whichever role the user originally signed up as (the legacy
 * `user.role` column, untouched by later role grants) comes first.
 */
const WorkspaceToggle = ({ role, reviewStatus }: { role: UserRole; reviewStatus?: string }) => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const showReviewDot = role === "advisor" && !!reviewStatus && reviewStatus !== "APPROVED";

  const switchMutation = useMutation({
    mutationFn: (workspace: UserRole) => api.users.setActiveWorkspace(workspace),
    onSuccess: async () => {
      await refreshUser();
      navigate("/home");
    },
  });

  const becomeAchieverMutation = useMutation({
    mutationFn: async () => {
      await api.users.grantRole("achiever");
      await api.users.setActiveWorkspace("achiever");
    },
    onSuccess: async () => {
      await refreshUser();
      navigate("/home");
    },
  });

  if (!hasRole(user, "advisor")) {
    return null;
  }

  const ACHIEVER_TAB = { value: "achiever" as UserRole, label: "Achiever" };
  // "Expert", matching the rest of the product (admin panel, messages,
  // profiles, emails).
  const EXPERT_TAB = { value: "advisor" as UserRole, label: "Expert" };
  const tabs = user?.role === "advisor" ? [EXPERT_TAB, ACHIEVER_TAB] : [ACHIEVER_TAB, EXPERT_TAB];
  const isPending = switchMutation.isPending || becomeAchieverMutation.isPending;
  // While a switch is in flight the clicked tab shows as selected straight
  // away. The toggle used to disable BOTH tabs for the whole round trip
  // (PATCH + /users/me refresh), which dimmed them and turned the cursor into
  // the not-allowed sign — it read as broken rather than busy. Repeat clicks
  // are now ignored in the handler instead, and the cursor shows progress.
  const pendingTarget: UserRole | null = switchMutation.isPending
    ? (switchMutation.variables ?? null)
    : becomeAchieverMutation.isPending
      ? "achiever"
      : null;
  const shownRole = pendingTarget ?? role;

  return (
    /* The design drops the "Workspace" caption above the pills — the two
       labels already say what the control is, and the caption was costing a
       row of height at the top of every screen. */
    <div className="border-b border-theme-nav-divider px-4 pb-[14px] pt-[18px]">
      <div className="flex h-11 items-start gap-[3px] rounded-[14px] border border-theme-nav-toggle-border bg-theme-nav-toggle-bg p-[3px]">
        {tabs.map((tab) => {
          const active = tab.value === shownRole;
          const held = hasRole(user, tab.value);
          const selfServe = tab.value === "achiever"; // no approval needed, unlike advisor
          // Unavailable is a fact about the user (no such role, needs approval),
          // never a passing state like an in-flight switch.
          const unavailable = !held && !selfServe;
          const clickable = !active && !isPending && !unavailable;
          return (
            <button
              key={tab.value}
              type="button"
              disabled={unavailable}
              aria-pressed={active}
              aria-busy={isPending && active ? true : undefined}
              onClick={() => {
                if (!clickable) return;
                if (held) {
                  switchMutation.mutate(tab.value);
                } else {
                  becomeAchieverMutation.mutate();
                }
              }}
              title={
                unavailable
                  ? "You don't have this workspace yet"
                  : active
                    ? undefined
                    : `Switch to ${tab.label}`
              }
              className={cn(
                "relative flex h-9 flex-1 items-center justify-center rounded-[10px] text-[13px] font-semibold tracking-[-0.1px] transition-colors",
                active
                  ? "bg-theme-nav-toggle-active-bg text-theme-nav-toggle-active-text"
                  : unavailable
                    ? "cursor-not-allowed text-theme-nav-toggle-inactive-text opacity-60"
                    : "text-theme-nav-toggle-inactive-text hover:bg-theme-nav-toggle-hover-bg hover:text-theme-nav-item-active-text",
                !unavailable && (isPending ? "cursor-progress" : !active && "cursor-pointer"),
              )}
            >
              {tab.label}
              {tab.value === "advisor" && showReviewDot && (
                <Star
                  aria-label="Expert application not yet approved"
                  className="absolute right-1.5 top-1 h-2.5 w-2.5 fill-goals-achieved-gold text-goals-achieved-gold"
                  strokeWidth={2}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
  isCollapsed?: boolean;
  tourId?: string;
  /** Renders a count pill on the right (a dot when collapsed). 0/undefined shows nothing. */
  badgeCount?: number;
  /** Items inside a group are indented to sit under its heading. */
  isNested?: boolean;
}

/**
 * One nav row.
 *
 * Selected state is the design's: a tinted ground plus a 2px rail down the
 * left edge, drawn with an inset box-shadow rather than a border so the row's
 * own height and padding are unchanged between states — a border would shift
 * the label by 2px every time the route changed.
 */
const SidebarItem = ({
  icon,
  label,
  isActive = false,
  onClick,
  isCollapsed = false,
  tourId,
  badgeCount,
  isNested = false,
}: SidebarItemProps) => (
  <button
    onClick={onClick}
    data-tour-id={tourId}
    aria-current={isActive ? "page" : undefined}
    className={cn(
      "flex w-full items-center gap-3 rounded-[9px] py-2 pr-3 font-inter text-[15px] transition-colors",
      isCollapsed ? "justify-center px-3" : isNested ? "pl-7" : "pl-3",
      isActive
        ? "bg-theme-nav-item-active-bg font-bold text-theme-nav-item-active-text shadow-[inset_2px_0_0_0_var(--theme-nav-item-active-marker)]"
        : "font-medium text-theme-nav-item-text hover:bg-theme-nav-item-hover-bg hover:text-theme-nav-item-active-text",
    )}
    title={isCollapsed ? label : undefined}
  >
    <span
      className={cn(
        "relative flex h-5 w-5 flex-shrink-0 items-center justify-center",
        // The icon is a step lighter than the label when idle, per the design
        // (Cloudy against Tapa); on the selected row both take the accent.
        isActive ? "text-theme-nav-item-active-text" : "text-theme-nav-item-icon",
      )}
    >
      {icon}
      {/* Collapsed, there is no room for the number -- so it becomes a dot.
          Losing the count is fine; losing the fact that something is waiting
          is not, and that is what makes someone expand the sidebar. */}
      {isCollapsed && !!badgeCount && (
        <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary-500" />
      )}
    </span>
    {!isCollapsed && (
      <>
        <span className="truncate">{label}</span>
        {!!badgeCount && (
          <span
            className="ml-auto min-w-[20px] rounded-full bg-primary-500 px-1.5 text-center font-inter text-[11px] font-semibold leading-[18px] text-white"
            aria-label={`${badgeCount} awaiting review`}
          >
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </>
    )}
  </button>
);

/**
 * A labelled, collapsible block of items — the design's "NETWORK" heading.
 *
 * Collapsed out of view entirely when the sidebar is collapsed to its rail
 * width: the heading is the only thing that says what the indented items
 * belong to, and without it they read as unexplained extra icons. The items
 * themselves still render, just without the grouping.
 */
const SidebarGroup = ({
  group,
  isCollapsed,
  renderItem,
}: {
  group: SidebarGroupConfig;
  isCollapsed: boolean;
  renderItem: (item: SidebarItemConfig, isNested: boolean) => React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(group.defaultOpen ?? true);

  if (isCollapsed) {
    return <>{group.items.map((item) => renderItem(item, false))}</>;
  }

  return (
    <div className="pt-3">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className={cn(
          "flex w-full items-center gap-[6px] rounded-[9px] py-1.5 pl-4 pr-3 transition-colors hover:bg-theme-nav-item-hover-bg",
          // The mark and the label share the group's accent — the icons are
          // filled with currentColor, so one class colours both.
          group.accentClassName,
        )}
      >
        <span className="flex flex-shrink-0 items-center justify-center">{group.icon}</span>
        <span className="font-inter text-[11px] font-bold uppercase tracking-[0.99px]">
          {group.label}
        </span>
        <ChevronUp
          className={cn(
            "ml-auto h-3 w-3 text-theme-nav-item-icon transition-transform",
            !isOpen && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div className="mt-0.5 space-y-1">{group.items.map((item) => renderItem(item, true))}</div>
      )}
    </div>
  );
};

interface Props {
  role: UserRole;
  isCollapsed?: boolean;
}

export const NavigationSidebar = ({ role, isCollapsed = false }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = sidebarConfig[role];

  const { data: programTypes } = useQuery({
    queryKey: ["program-types"],
    queryFn: api.users.getProgramTypes,
    staleTime: 5 * 60 * 1000,
  });

  const { reviewStatus } = useAdvisorReviewStatus(role === "advisor");

  // Applications waiting on this facilitator, for the nav badge.
  //
  // Summed from the grouped listing the Programs page already fetches rather
  // than adding an endpoint: it carries pending_applications_count per program,
  // which is exactly this number. Advisors only -- an achiever facilitates
  // nothing, so there is nothing to count and no reason to fetch.
  const { data: peerGrouped } = useQuery({
    queryKey: ["peer-advisory-grouped-programs"],
    queryFn: peerAdvisoryApi.getGrouped,
    enabled: role === "advisor",
    staleTime: 60_000,
  });

  const pendingPeerApplications = useMemo(() => {
    if (!peerGrouped) return 0;
    // Only groups that are open: a draft or archived program cannot have
    // anything genuinely waiting.
    return [...peerGrouped.active, ...peerGrouped.in_review].reduce(
      (sum, program) => sum + (program.pending_applications_count ?? 0),
      0,
    );
  }, [peerGrouped]);

  const hasExpert = programTypes?.hasExpert ?? false;
  const hasPab = programTypes?.hasPab ?? false;
  const menuItems = getMenuItems(
    role,
    hasExpert,
    hasPab,
    reviewStatus === "APPROVED",
    pendingPeerApplications > 0,
  );

  // SUPERSEDED: the Organisation item below puts the program back in the nav.
  // // Organisation programs are deliberately NOT in the side nav: they are
  // // reached from Journey Portfolio and the Programs tab in each workspace.
  const groups = useMemo(() => getMenuGroups(role), [role]);

  // "Organisation": one item just above Profile, for members of an
  // Organisation program, opening the most recent one (the list is newest
  // first). Same in both workspaces.
  const hasOrganisation = programTypes?.hasOrganisation ?? false;
  const { data: myOrgPrograms } = useQuery({
    queryKey: orgProgramKeys.mine(),
    queryFn: organisationProgramApi.getMine,
    enabled: hasOrganisation,
    staleTime: 5 * 60 * 1000,
  });
  const currentOrgProgram = myOrgPrograms?.[0];
  const organisationItem = currentOrgProgram
    ? getOrganisationItem(
        currentOrgProgram.externalId,
        (myOrgPrograms ?? []).map((p) => p.externalId),
      )
    : null;

  const handleClick = (item: SidebarItemConfig) => {
    if (item.path) {
      navigate(item.path);
    }
  };

  const isItemActive = (item: SidebarItemConfig) => {
    // Items that deep-link with a query (several tabs sharing one path) are
    // active only on an exact path + query match, or every tab would light up.
    if (item.path?.includes("?")) {
      const [path, query] = item.path.split("?");
      const current = new URLSearchParams(location.search);
      const wanted = new URLSearchParams(query);
      return (
        location.pathname === path &&
        [...wanted.entries()].every(([key, value]) => (current.get(key) ?? "overview") === value)
      );
    }
    return (
      location.pathname === item.path ||
      (!!item.path && location.pathname.startsWith(item.path + "/")) ||
      (item.activePaths ?? []).includes(location.pathname)
    );
  };

  const renderItem = (item: SidebarItemConfig, isNested = false) => (
    <SidebarItem
      key={item.id}
      icon={item.icon}
      label={item.label}
      isActive={isItemActive(item)}
      onClick={() => handleClick(item)}
      isCollapsed={isCollapsed}
      tourId={item.tourId}
      isNested={isNested}
      badgeCount={item.id === "peer-applications" ? pendingPeerApplications : undefined}
    />
  );

  return (
    <div
      className={cn(
        "flex h-full flex-col border-r border-theme-nav-border bg-theme-nav-bg transition-all duration-300",
        // w-60 (240px), not 220px: this has to line up exactly with the header's
        // left block (`md:w-60` in Header.tsx), whose right border is the
        // vertical rule beside the logo. Any other value leaves the sidebar
        // edge and that rule visibly out of true down the whole page.
        isCollapsed ? "w-16" : "w-60",
      )}
    >
      {!isCollapsed && <WorkspaceToggle role={role} reviewStatus={reviewStatus} />}

      {/* Only the nav scrolls. The footer below used to live INSIDE this
          container, pushed down by mt-auto, which meant it drifted off the
          bottom as soon as the nav was taller than the viewport -- fine for a
          Help button, not for a progress dock that exists to stay visible. */}
      <div className="min-h-0 flex-1 overflow-y-auto py-4">
        <nav className="space-y-1 px-4">
          {menuItems.filter((item) => item.id !== "profile").map((item) => renderItem(item))}
          {groups.map((group) => (
            <SidebarGroup
              key={group.id}
              group={group}
              isCollapsed={isCollapsed}
              renderItem={renderItem}
            />
          ))}
          {/* Organisation, then Profile last, after the Network / Mindshare
              group, at the same level as the group rather than inside it. */}
          {organisationItem && renderItem(organisationItem)}
          {menuItems.filter((item) => item.id === "profile").map((item) => renderItem(item))}
        </nav>
      </div>

      {/* Pinned footer: the Getting Started dock above the design's circular
          Help button. The dock hides itself once the journey is complete. */}
      <div
        className={cn(
          "flex flex-col gap-3 border-t border-theme-nav-divider p-4",
          isCollapsed && "items-center",
        )}
      >
        {role === "advisor" && <GettingStartedDock isCollapsed={isCollapsed} />}

        <div className={cn(isCollapsed && "flex justify-center")}>
          {config.bottom.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleClick(item)}
              data-tour-id={item.tourId}
              title={item.label}
              aria-label={item.label}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-theme-nav-help-bg text-theme-nav-help-icon transition-opacity hover:opacity-80"
            >
              <span className="flex h-5 w-5 items-center justify-center">{item.icon}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
